import { LoggerName, getLogger } from "~/modules/logger";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { getReservationsByTask, canHold, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { FlexSdkError, ErrorCode, ErrorSeverity } from "~/modules/error";
import { Action } from "~/modules/actions/Action";




















export class UnholdParticipant implements Action<Promise<void>> {
    


    #targetParticipantSid: string;

    


    #taskSid: string;

    constructor(targetParticipantSid: string, taskSid: string) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`holdParticipant invoked with taskSid: ${this.#taskSid}`);

        if (!worker) {
            const errorMsg = "unholdParticipant: Worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");
        if (!reservation || !canHold(reservation, worker)) {
            const errorMsg = "unholdParticipant: Reservation not found or worker cannot unhold it";
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const task = reservation.task;
        const participants = await cbm.getParticipantsByTask(task);
        const participant = participants.find((p) => p.participantSid === this.#targetParticipantSid);

        if (participant && participant.type === "external") {
            try {
                await cbm.unholdParticipant(task, this.#targetParticipantSid);
                sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantSid: participant.participantSid,
                    participantType: participant.type
                });
                return Promise.resolve();
            } catch (error) {
                const errorMsg = `unholdParticipant: Could not unhold external participant ${
                    this.#targetParticipantSid
                }`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantSid: participant.participantSid,
                    participantType: participant.type
                });
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        const workerParticipant = participants.find(
            (p) => p.routingProperties?.workerSid === this.#targetParticipantSid
        );

        if (workerParticipant && workerParticipant.routingProperties?.workerSid && workerParticipant.type === "agent") {
            try {
                await task.hold(workerParticipant.routingProperties?.workerSid, false);
                sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantSid: workerParticipant.participantSid,
                    participantType: workerParticipant.type
                });
                return Promise.resolve();
            } catch (error) {
                const errorMsg = `unholdParticipant: Could not unhold agent participant ${this.#targetParticipantSid}`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid,
                    targetParticipantSid: this.#targetParticipantSid,
                    participantSid: workerParticipant.participantSid,
                    participantType: workerParticipant.type
                });
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        try {
            await task.updateParticipant({
                hold: false
            });
            sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant?.participantSid,
                participantType: participant?.type
            });
            return Promise.resolve();
        } catch (error) {
            const errorMsg = `unholdParticipant: Could not unhold participant ${this.#targetParticipantSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.UnholdParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant?.participantSid,
                participantType: participant?.type
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }
    }
}
